import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createWorkspaceSchema, type CreateWorkspaceInput } from '@wrapforce/shared';
import { Navigate, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useCreateWorkspace, useMe } from '@/features/session/api';
import { useSession } from '@/features/session/useSession';
import { useWorkspaceStore } from '@/stores/workspace';
import { AuthAlert } from '@/features/auth/AuthLayout';
import { apiErrorMessage } from '@/lib/api-errors';

const STEPS = ['Shop name', 'Starter catalog', 'Invite your team'] as const;

const CATALOG_PREVIEW_KEYS = ['vinylWrap', 'ppf', 'tint', 'ceramic', 'print'] as const;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function OnboardingWizard() {
  const { t } = useTranslation('onboarding');
  const [step, setStep] = useState(0);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { session } = useSession();
  const { setActiveWorkspaceId } = useWorkspaceStore();
  const createWorkspace = useCreateWorkspace();
  const { data: me } = useMe(!!session);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateWorkspaceInput>({ resolver: zodResolver(createWorkspaceSchema), defaultValues: { name: '', slug: '' } });

  const nameValue = watch('name');

  // Already onboarded (has a workspace) and not mid-wizard: keep /onboarding off-limits.
  if (me && me.memberships.length > 0 && step === 0 && !workspaceId) {
    return <Navigate to="/" replace />;
  }

  async function onStep1(values: CreateWorkspaceInput) {
    const result = await createWorkspace.mutateAsync(values);
    setWorkspaceId(result.workspaceId);
    if (session?.user.id) setActiveWorkspaceId(session.user.id, result.workspaceId);
    setStep(1);
  }

  function finish() {
    sessionStorage.setItem('wf-start-tour', '1');
    navigate('/', { replace: true });
  }

  return (
    <div className="grid min-h-screen place-items-center bg-bg px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="mb-5 flex items-center justify-center gap-2">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`grid h-6 w-6 place-items-center text-xs font-bold ${
                  i <= step ? 'bg-accent text-on-accent' : 'bg-surface-2 text-muted'
                }`}
              >
                {i + 1}
              </div>
              {i < STEPS.length - 1 ? <div className="h-px w-8 bg-border" /> : null}
            </div>
          ))}
        </div>

        <div className="rounded-[var(--radius-card)] border border-border bg-surface p-7 shadow-sm">
          {step === 0 ? (
            <form onSubmit={handleSubmit(onStep1)} className="flex flex-col gap-4" noValidate>
              <div>
                <h1 className="font-display text-xl font-bold text-ink">{t('step1.title')}</h1>
                <p className="mt-1 text-sm text-muted">{t('step1.subtitle')}</p>
              </div>
              {createWorkspace.isError ? (
                <AuthAlert>{apiErrorMessage(createWorkspace.error)}</AuthAlert>
              ) : null}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="name">{t('step1.shopNameLabel')}</Label>
                <Input
                  id="name"
                  {...register('name')}
                  onChange={(e) => {
                    setValue('name', e.target.value);
                    setValue('slug', slugify(e.target.value), { shouldValidate: true });
                  }}
                />
                <FieldError>{errors.name?.message}</FieldError>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="slug">{t('step1.workspaceUrlLabel')}</Label>
                <div className="flex items-center gap-1.5 text-sm text-muted">
                  <span>wrapforce.io/</span>
                  <Input id="slug" {...register('slug')} className="flex-1" />
                </div>
                <FieldError>{errors.slug?.message}</FieldError>
              </div>
              <Button type="submit" loading={createWorkspace.isPending} disabled={!nameValue} className="mt-2 w-full">
                {t('continue')}
              </Button>
            </form>
          ) : null}

          {step === 1 ? (
            <div className="flex flex-col gap-4">
              <div>
                <h1 className="font-display text-xl font-bold text-ink">{t('step2.title')}</h1>
                <p className="mt-1 text-sm text-muted">{t('step2.subtitle')}</p>
              </div>
              <div className="flex flex-col gap-3">
                {CATALOG_PREVIEW_KEYS.map((key) => (
                  <div key={key}>
                    <div className="text-xs font-bold uppercase tracking-wide text-muted">{t(`catalogPreview.${key}.service`)}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(t(`catalogPreview.${key}.items`, { returnObjects: true }) as string[]).map((item) => (
                        <span key={item} className="border border-border bg-surface-2 px-2.5 py-1 text-xs font-medium text-ink">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={() => setStep(2)} className="mt-2 w-full">
                {t('continue')}
              </Button>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="flex flex-col gap-4">
              <div>
                <h1 className="font-display text-xl font-bold text-ink">{t('step3.title')}</h1>
                <p className="mt-1 text-sm text-muted">
                  {t('step3.subtitle')}
                </p>
              </div>
              <Button onClick={finish} disabled={!workspaceId} className="mt-2 w-full">
                {t('step3.cta')}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
